/**
 * Audio engine — Web Audio API context, master bus, and layer management.
 *
 * All audio in TriggernometryDash is procedurally generated.
 * This module provides the central audio context, a master compressor/limiter,
 * and a layer system for dynamically adding/removing instrument groups.
 */

export interface AudioLayer {
  name: string;
  gain: GainNode;
  active: boolean;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private masterGain!: GainNode;
  private masterCompressor!: DynamicsCompressorNode;
  private layers = new Map<string, AudioLayer>();

  // Volume settings (0-1)
  masterVolume = 0.7;
  musicVolume = 0.5;
  sfxVolume = 0.8;

  /** Whether audio has been initialized (requires user gesture) */
  get initialized(): boolean {
    return this.ctx !== null && this.ctx.state !== 'closed';
  }

  get context(): AudioContext | null {
    return this.ctx;
  }

  get currentTime(): number {
    return this.ctx?.currentTime ?? 0;
  }

  /** Initialize the audio context (call on first user interaction) */
  init(): AudioContext {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') {
        this.ctx.resume();
      }
      return this.ctx;
    }

    this.ctx = new AudioContext();

    // Master compressor/limiter
    this.masterCompressor = this.ctx.createDynamicsCompressor();
    this.masterCompressor.threshold.value = -12;
    this.masterCompressor.knee.value = 6;
    this.masterCompressor.ratio.value = 4;
    this.masterCompressor.attack.value = 0.003;
    this.masterCompressor.release.value = 0.15;

    // Master gain
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = this.masterVolume;

    // Chain: layers → compressor → master gain → destination
    this.masterCompressor.connect(this.masterGain);
    this.masterGain.connect(this.ctx.destination);

    return this.ctx;
  }

  /** Resume audio context if suspended (e.g., after tab switch) */
  resume(): void {
    if (this.ctx?.state === 'suspended') {
      this.ctx.resume();
    }
  }

  /** Get or create a named audio layer */
  getLayer(name: string): AudioLayer {
    if (this.layers.has(name)) return this.layers.get(name)!;

    if (!this.ctx) throw new Error('Audio not initialized');

    const gain = this.ctx.createGain();
    gain.gain.value = this.musicVolume;
    gain.connect(this.masterCompressor);

    const layer: AudioLayer = { name, gain, active: true };
    this.layers.set(name, layer);
    return layer;
  }

  /** Enable a layer (fade in) */
  enableLayer(name: string, fadeTime = 0.5): void {
    const layer = this.layers.get(name);
    if (!layer || !this.ctx) return;
    layer.active = true;
    layer.gain.gain.cancelScheduledValues(this.ctx.currentTime);
    layer.gain.gain.linearRampToValueAtTime(
      this.musicVolume, this.ctx.currentTime + fadeTime,
    );
  }

  /** Disable a layer (fade out) */
  disableLayer(name: string, fadeTime = 0.5): void {
    const layer = this.layers.get(name);
    if (!layer || !this.ctx) return;
    layer.active = false;
    layer.gain.gain.cancelScheduledValues(this.ctx.currentTime);
    layer.gain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + fadeTime);
  }

  /** Get the master output node for SFX to connect to */
  get sfxOutput(): AudioNode {
    if (!this.ctx) throw new Error('Audio not initialized');
    return this.masterCompressor;
  }

  /** Create a gain node for SFX with proper volume */
  createSfxGain(): GainNode {
    if (!this.ctx) throw new Error('Audio not initialized');
    const gain = this.ctx.createGain();
    gain.gain.value = this.sfxVolume;
    gain.connect(this.masterCompressor);
    return gain;
  }

  /** Update master volume */
  setMasterVolume(v: number): void {
    this.masterVolume = v;
    if (this.masterGain) {
      this.masterGain.gain.value = v;
    }
  }

  /** Dip audio (on death) */
  dip(duration = 0.3): void {
    if (!this.ctx || !this.masterGain) return;
    const t = this.ctx.currentTime;
    this.masterGain.gain.cancelScheduledValues(t);
    this.masterGain.gain.setValueAtTime(this.masterVolume, t);
    this.masterGain.gain.linearRampToValueAtTime(this.masterVolume * 0.2, t + 0.05);
    this.masterGain.gain.linearRampToValueAtTime(this.masterVolume, t + duration);
  }

  /** Stop all audio */
  stop(): void {
    if (this.ctx) {
      this.ctx.close();
      this.ctx = null;
      this.layers.clear();
    }
  }
}
